import { useEffect, useId, useRef } from "react";
import { X } from "lucide-react";

// [Action confirmation standard](../../../docs/design/design-system.md#action-confirmation-standard):
// PostFlow owns activity changes and publication; this dialog only collects explicit confirmation.
// This prompt uses a corner close control to leave the publication action full width.
export function PostingActivityConfirmation({ activity, blurb, busy, error, onClose, onConfirm }: {
  activity: string;
  blurb: string;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const modal = dialog.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    modal?.showModal();
    heading.current?.focus({ preventScroll: true });
    return () => { modal?.close(); if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true }); };
  }, []);

  return <dialog ref={dialog} aria-labelledby={titleId} aria-describedby={descriptionId} aria-busy={busy}
    onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}
    className="m-auto max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-sm overflow-y-auto rounded-3xl border border-line bg-white p-6 text-center shadow-xl backdrop:bg-navy/50 sm:p-8">
    <button type="button" aria-label="Close confirmation" disabled={busy} onClick={onClose}
      className="absolute right-2 top-2 grid h-11 w-11 place-items-center rounded-full text-mut hover:bg-navy-50 hover:text-navy focus-visible:ring-2 focus-visible:ring-navy-500 disabled:opacity-50">
      <X className="h-5 w-5" aria-hidden="true" />
    </button>
    <h2 ref={heading} tabIndex={-1} id={titleId} className="pt-8 text-xl font-bold text-navy outline-none">Enable {activity} on your account?</h2>
    <p id={descriptionId} className="mt-3 break-words text-sm leading-6 text-mut">
      To publish this, we'll add the <span className="font-semibold text-navy">{activity}</span> activity so you can {blurb}. Your account keeps its existing activities.
    </p>
    {error && <p role="alert" className="mt-4 break-words text-sm text-brand-red">{error}</p>}
    <div className="mt-6">
      <button type="button" disabled={busy} onClick={onConfirm} className="btn-red h-auto min-h-11 min-w-0 w-full whitespace-normal px-3 py-2 text-sm disabled:opacity-50">{busy ? "Enabling…" : `Enable ${activity} & Publish`}</button>
    </div>
  </dialog>;
}
